import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  DailyReportDay,
  DailyReportEntry,
  DailyReportNote,
  SaveDailyReportNotePayload,
  SaveDailyReportPayload,
} from '@/types/dailyReport';

/**
 * Hands the Content-Type back to the browser for a multipart request.
 *
 * `apiClient` sets `application/json` for every call, which is right for all
 * but this one: with it in place the boundary is never written and the server
 * reads a multipart body as JSON. Setting it to `undefined` makes axios leave
 * the header off so the browser can write `multipart/form-data; boundary=…`.
 */
const MULTIPART = { headers: { 'Content-Type': undefined } };

/** The report as JSON in one text field, with the files beside it. */
function toFormData(payload: SaveDailyReportPayload, attachments: File[]): FormData {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  for (const file of attachments) form.append('attachments', file);

  return form;
}

/**
 * Every call the daily report screen makes.
 *
 * Its own file beside `recordings.api.ts`, for the same reason: a feature's
 * network layer that grows without bound is the one nobody can find anything in
 * (CLAUDE.md §6).
 *
 * Neither path carries a section id or an academic year. The day is resolved
 * from the caller's own allotments, so there is nothing in either request for
 * one teacher to read or write another's classes with.
 */
export const dailyReportsApi = {
  /**
   * The caller's classes on one day, each with whatever was written against it.
   *
   * One call rather than a timetable plus a set of reports joined in the
   * browser — which report belongs to which period is a rule, and it lives on
   * the server where every screen gets the same answer.
   *
   * Omitting the date means today, decided in IST by the server rather than by
   * whatever the teacher's laptop clock says.
   */
  day: (onDate?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<DailyReportDay>>('/institute/daily-reports', {
        params: onDate ? { onDate } : undefined,
      }),
    ),

  /**
   * Where one attachment is fetched from.
   *
   * A path rather than a call: the file is private (CLAUDE.md §11), so it is
   * streamed behind the same read check the report itself passes.
   */
  attachmentUrl: (uuid: string) => `/institute/daily-reports/attachments/${uuid}`,

  /**
   * Writes what was covered, or withdraws it when the topic is empty.
   *
   * A `PUT` because it is an upsert: the screen has one Save per class and does
   * not track whether a row exists. Resolves to `null` on a withdrawal.
   *
   * Multipart, because the files travel with it: a report half-saved — written,
   * but with its worksheet lost — is worse than one that failed outright. The
   * report itself goes in a JSON text field beside them, the same shape the
   * homework and admission forms use.
   */
  save: (payload: SaveDailyReportPayload, attachments: File[] = []) =>
    unwrap(
      apiClient.put<ApiSuccess<DailyReportEntry | null>>(
        '/institute/daily-reports',
        toFormData(payload, attachments),
        MULTIPART,
      ),
    ),

  /**
   * Adds one note about the day as a whole — never about a single lesson.
   *
   * A `POST` rather than the `PUT` above, and deliberately: a note is added to
   * the day, not upserted over yesterday's. Neither this nor the delete carries
   * a teacher id — the server takes it from the token, so there is nothing here
   * to reach another teacher's notes with. Read back through `day()`.
   */
  addNote: (payload: SaveDailyReportNotePayload) =>
    unwrap(apiClient.post<ApiSuccess<DailyReportNote>>('/institute/daily-reports/notes', payload)),

  /** Withdraws one of the caller's own notes. A soft delete on the server. */
  removeNote: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/daily-reports/notes/${uuid}`)),
};
